import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
    History,
    ChevronDown,
    ChevronUp,
    MapPin,
    FileText,
    Film,
    Users,
    Calendar as CalendarIcon,
    Activity,
    Radio,
    Package,
    Pencil,
} from "lucide-react";

const ACTION_META = {
    details_updated: { icon: MapPin, label: "Gegevens" },
    description_updated: { icon: Pencil, label: "Beschrijving" },
    script_updated: { icon: FileText, label: "Script" },
    status_changed: { icon: Activity, label: "Status" },
    schedule_updated: { icon: CalendarIcon, label: "Planning" },
    schedule_cancelled: { icon: CalendarIcon, label: "Annulering" },
    items_updated: { icon: Package, label: "Pakket" },
    co_agents_updated: { icon: Users, label: "Extra makelaars" },
    media_updated: { icon: Film, label: "Media" },
    live_toggled: { icon: Radio, label: "Live" },
};

const ROLE_LABEL = {
    admin: "Admin",
    marketing_manager: "Marketing",
    agent: "Makelaar",
};

const ROLE_STYLE = {
    admin: "bg-[#E5A93D]/20 text-[#E5A93D] border-[#E5A93D]/30",
    marketing_manager: "bg-blue-500/20 text-blue-700 border-blue-500/30",
    agent: "bg-zinc-200 text-zinc-600 border-zinc-300",
};

function fmtDateTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleString("nl-BE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export default function AuditLogPanel({ requestId }) {
    const [open, setOpen] = useState(false);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get(`/requests/${requestId}/audit-log`);
            setEntries(data.entries || []);
            setLoaded(true);
        } catch (e) {
            setError(e.response?.data?.detail || "Logboek laden mislukt");
        }
        setLoading(false);
    };

    useEffect(() => {
        if (open && !loaded) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md" data-testid="audit-log-panel">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between p-5 lg:p-6 text-left hover:bg-zinc-100 transition rounded-md"
                data-testid="audit-log-toggle"
            >
                <div className="flex items-center gap-2">
                    <History className="h-4 w-4 text-[#E5A93D]" />
                    <h2 className="font-display font-bold text-xl tracking-tight">Logboek</h2>
                    {loaded && entries.length > 0 && (
                        <span className="ml-2 text-[11px] text-zinc-500">({entries.length})</span>
                    )}
                </div>
                {open ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
            </button>

            {open && (
                <div className="px-5 lg:px-6 pb-6 border-t border-zinc-100">
                    <p className="text-xs text-zinc-500 pt-4 mb-3">
                        Alle wijzigingen aan dit project — wie heeft wat aangepast en wanneer.
                    </p>
                    {loading && <div className="text-sm text-zinc-500 py-4">Laden…</div>}
                    {error && <div className="text-sm text-red-600 py-4" data-testid="audit-log-error">{error}</div>}
                    {!loading && !error && entries.length === 0 && loaded && (
                        <div className="text-sm text-zinc-500 py-4">Nog geen activiteit geregistreerd.</div>
                    )}
                    {!loading && entries.length > 0 && (
                        <ul className="space-y-3" data-testid="audit-log-entries">
                            {entries.map((e) => {
                                const meta = ACTION_META[e.action] || { icon: Activity, label: e.action };
                                const Icon = meta.icon;
                                const roleStyle = ROLE_STYLE[e.user_role] || ROLE_STYLE.agent;
                                return (
                                    <li
                                        key={e.id}
                                        className="flex gap-3 p-3 bg-[#F4F4F5] border border-zinc-100 rounded-sm"
                                        data-testid="audit-log-entry"
                                    >
                                        <div className="h-8 w-8 flex-shrink-0 rounded-sm bg-[#E5A93D]/10 border border-[#E5A93D]/20 flex items-center justify-center">
                                            <Icon className="h-4 w-4 text-[#E5A93D]" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-sm font-medium text-zinc-950">{e.user_name}</span>
                                                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${roleStyle}`}>
                                                    {ROLE_LABEL[e.user_role] || e.user_role}
                                                </span>
                                                <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                                                    {meta.label}
                                                </span>
                                            </div>
                                            <div className="text-sm text-zinc-600 mt-1 break-words">{e.summary}</div>
                                            <div className="text-[11px] text-zinc-500 mt-1">{fmtDateTime(e.created_at)}</div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
